import React, { ReactElement, useMemo } from 'react';
import {
  Box,
  FormControlLabel,
  FormHelperText,
  Link,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';

import CustomSelectField from './CustomSelectField';
import { gray80, red50 } from '../../../constants/colors';
import { regularSm } from '../../../constants/location';
import { itemChoiceLabel } from '../../../constants/itemValidation';
import { ItemChoiceType } from '../../../types/common';
import { findMatchingChoice, resolveChoiceValue } from '../../../utils/pattern/choiceUtils';

/**
 * 選択肢項目（ラジオボタン・プルダウン）の入力欄（#3075）。
 *
 * 画面には表示名（label）を出し、フォームが保持するのは出力値（value）。
 * 出力値が未設定の選択肢は表示名がそのまま出力値になる。
 *
 * 選択肢定義を後から変更すると、既存の結果データが候補外になることがある。
 * その値を勝手に消すとユーザーの確認済みデータが失われるため、
 * 「(候補外) 〇〇」として選択肢の末尾に一時的に足して見せる。
 */

type Props = {
  /** 現在の値（出力値） */
  value: string;
  onChange: (value: string) => void;
  choices: ItemChoiceType[];
  /** 'radio' なら横並びのラジオボタン、'select' ならプルダウン */
  variant: 'radio' | 'select';
  /** 必須項目か（#2815）。必須なら未選択に戻す手段を出さない */
  required?: boolean;
  /** プルダウンの高さ。明細セルは行の高さに合わせて 40px を渡す */
  height?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
};

const ChoiceField = ({
  value,
  onChange,
  choices,
  variant,
  required = false,
  height = '56px',
  error = false,
  helperText,
  disabled = false,
  onFocus,
  onBlur,
}: Props): ReactElement => {
  // 候補外の値が入っている場合は選択肢の末尾に足して、値が消えないようにする
  const options = useMemo(() => {
    const base = choices.map(choice => ({
      value: resolveChoiceValue(choice),
      label: choice.label,
    }));

    const current = String(value ?? '').trim();
    if (current === '' || findMatchingChoice(current, choices) !== null) return base;

    return [...base, { value: current, label: itemChoiceLabel.outOfChoice(current) }];
  }, [choices, value]);

  if (variant === 'select') {
    return (
      <Box onFocus={onFocus} onBlur={onBlur}>
        <CustomSelectField
          labelText=""
          formMb="0px"
          // 未選択の状態へ戻せるよう、先頭に空の選択肢を置く。
          // 必須項目では未選択にしても保存できないため出さない（ラジオの解除リンクと揃える）
          list={[
            ...(required ? [] : [{ value: '', option: itemChoiceLabel.unselected }]),
            ...options.map(opt => ({ value: opt.value, option: opt.label })),
          ]}
          value={value ?? ''}
          onChange={(event): void => onChange(String(event.target.value ?? ''))}
          width="100%"
          height={height}
          fontSize="14px"
          error={error}
          disabled={disabled}
        />
        {helperText && (
          <FormHelperText sx={{ color: error ? red50 : gray80, marginLeft: '14px' }}>
            {helperText}
          </FormHelperText>
        )}
      </Box>
    );
  }

  const hasValue = (value ?? '') !== '';

  return (
    <Box onFocus={onFocus} onBlur={onBlur}>
      <RadioGroup
        row
        value={value ?? ''}
        onChange={(event): void => onChange(event.target.value)}
        // 選択肢が多いと1行に収まらないため折り返す。
        // 折り返し時に行同士が詰まらないよう行間も取る。
        // 各要素（ラジオ・ラベル・解除リンク）は上下中央で一直線に揃える
        sx={{ flexWrap: 'wrap', alignItems: 'center', gap: '4px 16px' }}
      >
        {options.map(option => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio size="small" disabled={disabled} />}
            label={
              <Typography sx={{ fontSize: '14px', color: gray80, textAlign: 'left' }}>
                {option.label}
              </Typography>
            }
            // ラジオとテキストは上下中央で揃える（他の選択肢・解除リンクと一直線にする）。
            // 表示名が長く折り返す場合に備え、テキスト自体は左揃えにしておく
            sx={{ marginRight: 0 }}
          />
        ))}

        {/* ラジオボタンは HTML の仕様上いちど選ぶと全解除できない。
            読み取れず空欄だった項目を誤って選んだときに戻せるよう、解除手段を添える（#3075）。
            最後の選択肢の右に並べる。選択肢と同じ流れに置くことで、
            折り返しても選択肢の直後に続き、離れた位置に取り残されない。
            必須項目では未選択にしても保存できないため出さない。
            未選択のときも押す意味がないので出さない */}
        {hasValue && !required && !disabled && (
          <Link
            component="button"
            type="button"
            onClick={(): void => onChange('')}
            sx={{
              ...regularSm,
              color: gray80,
              textDecoration: 'underline',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {itemChoiceLabel.clear}
          </Link>
        )}
      </RadioGroup>

      {helperText && (
        <FormHelperText sx={{ color: error ? red50 : gray80 }}>{helperText}</FormHelperText>
      )}
    </Box>
  );
};

export default ChoiceField;
